import type { IDisposable } from "@codesandbox/pitcher-client";
import { Disposable } from "@codesandbox/pitcher-client";
import type { IRange } from "monaco-editor";
import type * as VSCode from "vscode";
import {
  ICodeEditorService,
  IEditorService,
  StandaloneServices,
} from "vscode/services";
import type { Selection } from "vscode/vscode/vs/editor/common/core/selection";

export interface IHistoryEntry {
  model:
    | {
        type: "code";
        language: string;
        uri: VSCode.Uri;
      }
    | {
        type: "diff";
        language: string;
        originalUri: VSCode.Uri;
        modifiedUri: VSCode.Uri;
      };
  selection: Selection | null;
}

/**
 * Adds support for "Go Back" and "Go Forward" inside the editor. This one is very similar
 * to IHistoryService from VSCode (which implements the same thing), but works with our
 * code editor service. Kind of like a poor man's history service.
 */
export class EditorHistoryService extends Disposable {
  private codeEditorService: ICodeEditorService;
  private editorService: IEditorService;

  private history: IHistoryEntry[] = [];
  private historyCursor = -1;

  private ignoreNextPushState = false;

  constructor() {
    super();

    this.codeEditorService = StandaloneServices.get(ICodeEditorService);
    this.editorService = StandaloneServices.get(IEditorService);

    this.initializeListeners();

    if (this.editorService.activeTextEditorControl) {
      this.pushState();
    }
  }

  getHistory() {
    return this.history;
  }

  private initializeListeners() {
    this.addDisposable(
      this.editorService.onDidActiveEditorChange(() => {
        this.pushState();

        this.registerEditorListeners();
      }),
    );
  }

  private activeEditorDisposable: IDisposable | null = null;
  private registerEditorListeners() {
    if (this.activeEditorDisposable) {
      this.activeEditorDisposable.dispose();
    }

    const editor = this.codeEditorService.getFocusedCodeEditor();
    if (!editor) {
      return;
    }

    this.activeEditorDisposable = this.addDisposable(
      editor.onDidChangeCursorSelection((evt) => {
        const lastSelection = this.history[this.historyCursor].selection;

        if (!isSimilarSelection(lastSelection, evt.selection)) {
          this.pushState();
        }
      }),
    );
  }

  private pushState() {
    if (this.ignoreNextPushState) {
      this.ignoreNextPushState = false;
      return;
    }

    const currentEditor = this.editorService.activeTextEditorControl;
    if (!currentEditor) {
      return;
    }

    const model = currentEditor.getModel();
    if (!model) {
      return;
    }

    const selection = currentEditor.getSelection();
    if (!selection) {
      return;
    }

    // Remove all next states
    this.history.length = this.historyCursor + 1;

    if ("uri" in model) {
      this.history.push({
        model: {
          type: "code",
          language: model.getLanguageId(),
          uri: model.uri,
        },
        // @ts-ignore
        selection,
      });
    } else {
      this.history.push({
        model: {
          type: "diff",
          // @ts-ignore
          language: model.modified.getLanguageId(),
          // @ts-ignore
          originalUri: model.original.uri,
          // @ts-ignore
          modifiedUri: model.modified.uri,
        },
        // @ts-ignore
        selection,
      });
    }

    if (this.history.length > 100) {
      this.history.shift();
    }

    this.historyCursor = this.history.length - 1;
  }
}

// Taken from VSCode
const TEXT_EDITOR_SELECTION_THRESHOLD = 10;

function isSimilarSelection(
  previousSelection: IRange | null,
  currentSelection: IRange | null,
) {
  if (!previousSelection || !currentSelection) {
    return false;
  }

  const thisLineNumber = Math.min(
    previousSelection.startLineNumber,
    previousSelection.endLineNumber,
  );
  const otherLineNumber = Math.min(
    currentSelection.startLineNumber,
    currentSelection.endLineNumber,
  );

  return (
    Math.abs(thisLineNumber - otherLineNumber) < TEXT_EDITOR_SELECTION_THRESHOLD
  );
}
